import React from 'react';
import { Box, Skeleton, styled } from '@mui/material';
import { useAppSelector } from 'store/hooks';
import { Role } from 'components/merchant/models/Role';
import { RoleCard } from './RoleCard';
import { RoleDetailsDrawer } from './RoleDetailsDrawer';
import { TOTAL_VIEWS_COUNT, countAccessibleViews, getRoleDisplay } from './helpers';

interface RolesTabProps {
    currentRoles: Role[] | null;
}

const Grid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: theme.spacing(2),
    padding: theme.spacing(2)
}));

export const RolesTab: React.FC<RolesTabProps> = ({ currentRoles }) => {
    const { tenantUsersAccess } = useAppSelector(state => state.tenantAccess);
    const [selectedRoleId, setSelectedRoleId] = React.useState<string | null>(null);

    const userCountByRole = React.useMemo(() => {
        const counts = new Map<string, number>();
        for (const access of tenantUsersAccess ?? []) {
            counts.set(access.roleId, (counts.get(access.roleId) ?? 0) + 1);
        }
        return counts;
    }, [tenantUsersAccess]);

    const handleSelectRole = React.useCallback((roleId: string) => () => setSelectedRoleId(roleId), []);
    const handleCloseDrawer = React.useCallback(() => setSelectedRoleId(null), []);

    const selectedRole = React.useMemo(
        () => currentRoles?.find(role => role.id === selectedRoleId) ?? null,
        [currentRoles, selectedRoleId]
    );

    if (!currentRoles) {
        return (
            <Grid>
                {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={144} />
                ))}
            </Grid>
        );
    }

    return (
        <React.Fragment>
            <Grid sx={{ p: 0, mt: 2 }}>
                {currentRoles.map(role => (
                    <RoleCard
                        key={role.id}
                        display={getRoleDisplay(role.id, role.name)}
                        userCount={userCountByRole.get(role.id) ?? 0}
                        accessiblePages={countAccessibleViews(role.permissions)}
                        totalPages={TOTAL_VIEWS_COUNT}
                        onClick={handleSelectRole(role.id)}
                    />
                ))}
            </Grid>
            <RoleDetailsDrawer
                open={!!selectedRole}
                display={selectedRole ? getRoleDisplay(selectedRole.id, selectedRole.name) : null}
                permissions={selectedRole?.permissions ?? null}
                userCount={selectedRole ? userCountByRole.get(selectedRole.id) ?? 0 : 0}
                onClose={handleCloseDrawer}
            />
        </React.Fragment>
    );
};
